// ============================================================================
// minimap.js — canvas overlay (bottom-right). Troop-density blobs from every
// active unit, bastions coloured by owner, gates, named warlords, the Dynast,
// the Warmaster and Benjen (a heading arrow). Throttled to ~20Hz.
// ============================================================================

import {
  MAP_SIZE, MAP_HALF, TEAM_PLAYER, TEAM_MAGMAR, ST_DEAD, UNIT_WARLORD,
} from '../core/constants.js';
import { EV } from '../core/events.js';

const TONE_COL = { good: '#7dff8a', bad: '#ff4a3a', neutral: '#ffd07a' };

export class Minimap {
  constructor(hudRoot, world) {
    this.world = world;
    this.size = 190;
    const c = document.createElement('canvas');
    c.id = 'minimap';
    c.width = c.height = this.size;
    c.style.cssText = `position:fixed;right:20px;bottom:20px;width:${this.size}px;height:${this.size}px;
      border:1px solid rgba(255,106,26,.4);background:rgba(6,4,3,.62);border-radius:4px;box-shadow:0 4px 18px rgba(0,0,0,.6);z-index:12;`;
    hudRoot.appendChild(c);
    this.ctx = c.getContext('2d');
    this.scale = this.size / MAP_SIZE;
    this._t = 0;

    // event pings — expanding rings that draw the eye to where something happened
    this.pings = [];
    const ev = world.events;
    const push = (x, z, tone, dur) => this.pings.push({ x, z, t: 0, dur: dur || 1.8, col: TONE_COL[tone] || TONE_COL.neutral });
    ev.on(EV.PING, ({ x, z, tone }) => push(x, z, tone));
    ev.on(EV.BASTION_CAPTURED, ({ bastion, team }) => push(bastion.x, bastion.z, team === TEAM_PLAYER ? 'good' : 'bad', 2.4));
    ev.on(EV.WARLORD_ENGAGED, ({ warlord }) => push(warlord.x, warlord.z, 'bad', 2.0));
    ev.on(EV.WARLORD_SLAIN, ({ warlord }) => { if (warlord) push(warlord.x, warlord.z, warlord.warlord && warlord.warlord.ally ? 'bad' : 'good', 2.2); });
    ev.on(EV.BATTLE_START, () => { this.pings.length = 0; });
  }

  _p(x) { return (x + MAP_HALF) * this.scale; }

  update(world, dt) {
    // advance event-ping timers every frame (smooth even though the map redraws at 20Hz)
    for (let i = this.pings.length - 1; i >= 0; i--) { const pg = this.pings[i]; pg.t += dt; if (pg.t >= pg.dur) this.pings.splice(i, 1); }

    this._t -= dt;
    if (this._t > 0) return;
    this._t = 0.05;
    const g = this.ctx, S = this.size;
    g.clearRect(0, 0, S, S);

    // roads
    g.strokeStyle = 'rgba(120,70,40,.25)'; g.lineWidth = 2;
    g.beginPath(); g.moveTo(this._p(0), 0); g.lineTo(this._p(0), S); g.stroke();

    // troop density — every active unit a faint dot
    const units = world.units.active;
    for (let i = 0; i < units.length; i++) {
      const u = units[i];
      if (u.state === ST_DEAD) continue;
      g.fillStyle = u.team === TEAM_PLAYER ? 'rgba(150,170,210,.5)' : 'rgba(220,80,40,.5)';
      g.fillRect(this._p(u.x) - 0.5, this._p(u.z) - 0.5, 1.6, 1.6);
    }

    // bastions
    for (let i = 0; i < world.bastions.length; i++) {
      const b = world.bastions[i];
      g.fillStyle = b.team === TEAM_PLAYER ? '#aeb6c4' : '#ff5a1a';
      const bx = this._p(b.x), bz = this._p(b.z);
      g.fillRect(bx - 4, bz - 4, 8, 8);
      g.strokeStyle = 'rgba(0,0,0,.7)'; g.lineWidth = 1; g.strokeRect(bx - 4, bz - 4, 8, 8);
    }

    // gates
    if (world.gates) for (const gt of world.gates) {
      g.fillStyle = gt.broken ? '#5a4a3a' : '#c89a5a';
      g.fillRect(this._p(gt.x) - 2, this._p(gt.z) - 2, 4, 4);
    }

    // warlords + dynast
    for (let i = 0; i < world.warlords.length; i++) {
      const w = world.warlords[i];
      if (!w._active || w.state === ST_DEAD) continue;
      this._diamond(g, this._p(w.x), this._p(w.z), 3.5, '#ffb257');
    }
    if (world.dynast && world.dynast._active && world.dynast.state !== ST_DEAD) {
      this._diamond(g, this._p(world.dynast.x), this._p(world.dynast.z), 5, '#ff2a2a');
    }
    if (world.warmaster && world.warmaster._active && world.warmaster.state !== ST_DEAD) {
      this._diamond(g, this._p(world.warmaster.x), this._p(world.warmaster.z), 4, '#8ab4ff');
    }

    // player arrow
    const p = world.player;
    const px = this._p(p.x), pz = this._p(p.z);
    g.save();
    g.translate(px, pz);
    g.rotate(-p.heading + Math.PI); // heading 0 = +Z (down on map)
    g.fillStyle = '#fff';
    g.beginPath(); g.moveTo(0, -6); g.lineTo(4, 5); g.lineTo(-4, 5); g.closePath(); g.fill();
    g.restore();

    // event pings — expanding sonar rings on top of everything
    for (let i = 0; i < this.pings.length; i++) {
      const pg = this.pings[i];
      const px = this._p(pg.x), pz = this._p(pg.z);
      const k = pg.t / pg.dur;                 // 0..1
      for (let r = 0; r < 2; r++) {            // two staggered rings
        const kk = k - r * 0.25;
        if (kk < 0 || kk > 1) continue;
        g.globalAlpha = (1 - kk) * 0.9;
        g.strokeStyle = pg.col; g.lineWidth = 1.6;
        g.beginPath(); g.arc(px, pz, 2 + kk * 15, 0, 6.2832); g.stroke();
      }
      // solid core dot for the first half of its life
      if (k < 0.5) { g.globalAlpha = 1 - k * 2; g.fillStyle = pg.col; g.beginPath(); g.arc(px, pz, 2.4, 0, 6.2832); g.fill(); }
      g.globalAlpha = 1;
    }

    // frame border tint by morale
    g.strokeStyle = world.morale >= 50 ? 'rgba(150,170,210,.4)' : 'rgba(220,80,40,.4)';
    g.lineWidth = 2; g.strokeRect(1, 1, S - 2, S - 2);
  }

  _diamond(g, x, z, r, col) {
    g.fillStyle = col;
    g.beginPath();
    g.moveTo(x, z - r); g.lineTo(x + r, z); g.lineTo(x, z + r); g.lineTo(x - r, z); g.closePath();
    g.fill();
    g.strokeStyle = 'rgba(0,0,0,.8)'; g.lineWidth = 1; g.stroke();
  }
}
